import { useLocalStorage } from 'usehooks-ts';
import { TESTNET_FORM_URL } from '../../../constants';
import { useDepositsEnabled } from '../../../hooks/useDepositsEnabled';
import { useModalState } from '../../../hooks/useModalState';
import { ProductType } from '../../../hooks/useProducts';
import { useTradeFormAccountSummaryData } from '../../../hooks/useTradeFormAccountSummary';
import {
  formatDollar,
  formatLargeDollar,
  formatLargeNumber,
  formatPercentage,
} from '../../../lib/formatters';
import { Gwei } from '../../../lib/gwei';
import { localStorageKeys } from '../../../lib/localstorageKeys';
import {
  accountEquityTooltip,
  accountLeverageTooltip,
  maintenanceMarginTooltip,
  marginUsageTooltip,
} from '../../../lib/tooltips';
import { Button } from '../../catalyst-ui-kit/button';
import { Divider } from '../../catalyst-ui-kit/divider';
import { HealthIcon } from '../../HealthIcon/HealthIcon';
import { getHealthColorClass } from '../../HealthIcon/healthThreshold';
import { HidePnlBtn } from '../../HidePnlBtn/HidePnlBtn';
import { Tooltip } from '../../Tooltip/Tooltip';
import { WithdrawButton } from '../../WithdrawButton/WithdrawButton';
import { StatRow, StatRowValue, StatRowValueDiff } from './TradeFormStats';

type DisconnectedNoticeProps = {
  message: string;
};

function DisconnectedNotice({ message }: DisconnectedNoticeProps) {
  return (
    <div>
      <div className="p-3 py-4">
        <p className="text-xs font-medium">Account</p>
      </div>
      <Divider />
      <div className="flex size-full h-full items-center justify-center px-3 py-5">
        <p className="max-w-48 text-center text-xs text-contentSecondary">{message}</p>
      </div>
    </div>
  );
}

type TradeFormAccountSummaryUiProps = {
  currentAccountLeverage?: bigint;
  projectedAccountLeverage?: bigint;
  marginUsage?: bigint;
  projectedMarginUsage?: bigint;
  maintenanceMargin?: bigint;
  projectedMaintenanceMargin?: bigint;
  accountEquity?: bigint;
  accountPnl?: bigint;
  isConnected: boolean;
  hasSubaccount: boolean;
  hidePnl: boolean;
  handleDeposit: () => void;
};

export const TradeFormAccountSummaryUi: React.FC<TradeFormAccountSummaryUiProps> = ({
  isConnected,
  hasSubaccount,
  hidePnl,
  handleDeposit,
  accountEquity,
  accountPnl,
  maintenanceMargin,
  projectedMaintenanceMargin,
  marginUsage,
  projectedMarginUsage,
  currentAccountLeverage,
  projectedAccountLeverage,
}) => {
  if (!isConnected) {
    return <DisconnectedNotice message="Connect your wallet to deposit funds & start trading" />;
  }

  if (!hasSubaccount) {
    return <DisconnectedNotice message="Deposit funds & start trading" />;
  }

  return (
    <div>
      <div className="flex flex-col gap-4 p-3">
        <div className="grid grid-cols-2 gap-2">
          <Button className="flex-1" color="blue" onClick={handleDeposit}>
            Deposit
          </Button>
          <WithdrawButton className="flex-1" />
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium">Account</p>
          <HidePnlBtn size={16} />
        </div>
      </div>
      <Divider />
      <div className="flex flex-col gap-2 px-3 py-2">
        <StatRow
          label={<Tooltip content={accountEquityTooltip()}>Account Equity</Tooltip>}
          labelClassName="font-medium text-contentSecondary"
        >
          {hidePnl ? '******' : <StatRowValue value={accountEquity} formatValue={formatDollar} />}
        </StatRow>

        <StatRow label="Unrealized P&L" labelClassName="font-medium text-contentSecondary">
          {hidePnl ? (
            '******'
          ) : (
            <StatRowValue value={accountPnl} formatValue={formatDollar} colorised />
          )}
        </StatRow>
        <StatRow label={<Tooltip content={maintenanceMarginTooltip()}>Maintenance Margin</Tooltip>}>
          <StatRowValueDiff
            currentValue={maintenanceMargin}
            projectedValue={projectedMaintenanceMargin}
            formatValue={formatLargeDollar}
          />
        </StatRow>
        <StatRow label={<Tooltip content={marginUsageTooltip()}>Margin Usage</Tooltip>}>
          {marginUsage !== undefined && <HealthIcon marginUsage={Gwei.fromGwei(marginUsage)} />}
          <StatRowValueDiff
            currentValue={marginUsage}
            projectedValue={projectedMarginUsage}
            formatValue={(x) => (x > Gwei.toGwei('1') ? '100%+' : formatPercentage(x))}
            projectedClassName={
              projectedMarginUsage === undefined
                ? undefined
                : getHealthColorClass(Gwei.fromGwei(projectedMarginUsage))
            }
            currentClassName={
              marginUsage === undefined
                ? undefined
                : getHealthColorClass(Gwei.fromGwei(marginUsage))
            }
          />
        </StatRow>
        <StatRow label={<Tooltip content={accountLeverageTooltip()}>Account Leverage</Tooltip>}>
          <StatRowValueDiff
            currentValue={currentAccountLeverage}
            projectedValue={projectedAccountLeverage}
            formatValue={(x) => `${formatLargeNumber(x)}x`}
          />
        </StatRow>
      </div>
    </div>
  );
};

export const TradeFormAccountSummary: React.FC<{ product: ProductType }> = ({ product }) => {
  const depositsEnabled = useDepositsEnabled();

  const summary = useTradeFormAccountSummaryData(product);
  const [hidePnl] = useLocalStorage(localStorageKeys.hidePnl, false);
  const setDepositModal = useModalState((s) => s.setDepositModal);
  return (
    <TradeFormAccountSummaryUi
      isConnected={summary.isConnected}
      hasSubaccount={!!summary.selectedSubaccount}
      hidePnl={hidePnl}
      currentAccountLeverage={summary.currentAccountLeverage}
      projectedAccountLeverage={summary.projectedAccountLeverage}
      marginUsage={summary.marginUsage}
      projectedMarginUsage={summary.projectedMarginUsage}
      maintenanceMargin={summary.maintenanceMargin}
      projectedMaintenanceMargin={summary.projectedMaintenanceMargin}
      accountEquity={summary.accountEquity}
      accountPnl={summary.accountUPnl}
      handleDeposit={() => {
        if (!depositsEnabled) {
          location.href = TESTNET_FORM_URL;
          return;
        }
        return setDepositModal({ openOrClosed: 'open', context: 'Onboard' });
      }}
    />
  );
};
